import {
  DragHandle,
  SortableList,
} from "@circleco/compass/components/SortableList";
import { useCallback, useMemo, useState } from "react";

import {
  MAX_COMMUNITIES_TO_DISPLAY,
  NAVIGATION_TIMEOUT_MS,
} from "../constants";
import { useCommunitySwitcherContext } from "../context/CommunitySwitcherContext";
import { getAutomaticLoginRootUrl } from "../helpers/utils";
import { useCommunitySortState } from "../hooks/useCommunitySortState";
import type { CommunitySwitcherHotkeyEntry } from "../hooks/useCommunitySwitcherHotkeys";
import { useCommunitySwitcherHotkeys } from "../hooks/useCommunitySwitcherHotkeys";
import { useNavigationLoadingState } from "../hooks/useNavigationLoadingState";
import { useNotificationCountSync } from "../hooks/useNotificationCountSync";
import type { CommunitySwitcher } from "../types/CommunitySwitcher";
import { CommunitiesModal } from "./CommunitiesModal";
import { CommunitySection } from "./CommunitySection";
import { CommunitySwitcherPresentation } from "./CommunitySwitcherPresentation";

interface SortableCommunityItem {
  id: string;
  community: CommunitySwitcher;
  hotkeyPosition: number;
}

export const CommunitySwitcherContent = () => {
  const {
    api,
    featureFlags,
    theme,
    setCommunitiesNeedingCounts,
    isLoadingAppData,
  } = useCommunitySwitcherContext();
  const {
    communities,
    isLoadingCommunities,
    isReorderingCommunities,
    onSortCommunities,
  } = api;

  const { draggableItems, onSort, pinnedCurrentCommunity, sortedCommunities } =
    useCommunitySortState(communities, {
      isLoadingCommunities,
      isReorderingCommunities,
      onSortCommunities,
    });

  const { handleNavClick, clickedCommunityId } = useNavigationLoadingState({
    currentCommunityId: api.currentCommunity?.id,
    onCommunitySwitch: featureFlags?.onCommunitySwitch,
    timeoutMs: NAVIGATION_TIMEOUT_MS,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  useNotificationCountSync(
    sortedCommunities,
    draggableItems,
    pinnedCurrentCommunity,
    isModalOpen,
    setCommunitiesNeedingCounts,
  );

  // Navigation no longer drives a skeleton — the optimistic highlight is the
  // click feedback, so only genuine data loads blank the switcher.
  const isLoading =
    (isLoadingAppData || isLoadingCommunities) && (theme.isLargeScreen ?? true);

  const shouldShowShowMoreButton =
    sortedCommunities.length > MAX_COMMUNITIES_TO_DISPLAY;

  const onClickSeeMyCommunities = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const pinnedCommunityPosition = useMemo(
    () =>
      pinnedCurrentCommunity
        ? sortedCommunities.indexOf(pinnedCurrentCommunity) + 1
        : undefined,
    [pinnedCurrentCommunity, sortedCommunities],
  );

  const hotkeyEntries = useMemo<CommunitySwitcherHotkeyEntry[]>(() => {
    const entries: CommunitySwitcherHotkeyEntry[] = draggableItems.map(
      (community, index) => ({ community, position: index + 1 }),
    );
    if (pinnedCurrentCommunity && pinnedCommunityPosition !== undefined) {
      entries.push({
        community: pinnedCurrentCommunity,
        position: pinnedCommunityPosition,
      });
    }
    return entries;
  }, [draggableItems, pinnedCurrentCommunity, pinnedCommunityPosition]);

  const handleHotkeyActivate = useCallback(
    (community: CommunitySwitcher) => {
      if (!community.is_current_community) {
        featureFlags?.onCommunitySwitch?.();
      }
      globalThis.location.href = getAutomaticLoginRootUrl(
        community.root_url,
        api.token,
      );
    },
    [api.token, featureFlags?.onCommunitySwitch],
  );

  useCommunitySwitcherHotkeys({
    entries: hotkeyEntries,
    onActivate: handleHotkeyActivate,
    onShowAll: shouldShowShowMoreButton ? onClickSeeMyCommunities : undefined,
  });

  const isDragDisabled =
    isReorderingCommunities || sortedCommunities.length <= 1;

  const sortableItems = useMemo<SortableCommunityItem[]>(
    () =>
      draggableItems.map((community, index) => ({
        id: String(community.id),
        community,
        hotkeyPosition: index + 1,
      })),
    [draggableItems],
  );

  const handleItemsChange = useCallback(
    (next: SortableCommunityItem[]) => {
      onSort(next.map(item => item.community));
    },
    [onSort],
  );

  return (
    <>
      <CommunitySwitcherPresentation
        id="community-switcher"
        className="hidden lg:block"
        isLoading={isLoading}
        shouldShowShowMoreButton={shouldShowShowMoreButton}
        onClick={handleNavClick}
        onClickSeeMyCommunities={onClickSeeMyCommunities}
      >
        <div className="flex flex-col gap-2">
          <SortableList<SortableCommunityItem>
            activationConstraints={{ distance: 8, delay: { duration: 150 } }}
            mode="flat"
            axis="vertical"
            className="flex flex-col gap-2"
            draggingClassName="opacity-40"
            items={sortableItems}
            onItemsChange={handleItemsChange}
            renderItem={({ community, hotkeyPosition }) => (
              <div>
                {isDragDisabled ? (
                  <CommunitySection
                    community={community}
                    hotkeyPosition={hotkeyPosition}
                    clickedCommunityId={clickedCommunityId}
                  />
                ) : (
                  <DragHandle
                    render={({ ref, style }) => (
                      <CommunitySection
                        community={community}
                        hotkeyPosition={hotkeyPosition}
                        clickedCommunityId={clickedCommunityId}
                        dragHandleRef={ref}
                        dragHandleStyle={style}
                      />
                    )}
                  />
                )}
              </div>
            )}
          />
          {pinnedCurrentCommunity ? (
            <CommunitySection
              community={pinnedCurrentCommunity}
              hotkeyPosition={pinnedCommunityPosition}
              clickedCommunityId={clickedCommunityId}
            />
          ) : null}
        </div>
      </CommunitySwitcherPresentation>
      <CommunitiesModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </>
  );
};
